/**
 * LaTeX Repair Utilities
 *
 * Fixes corrupted LaTeX escape sequences caused by LLM serialization
 * and converts College Board MathML alttext to LaTeX notation.
 *
 * Common corruption: When LaTeX like \frac gets stored through JSON/LLM pipelines,
 * the backslash sequences become C-style escape characters:
 *   \f → form feed (0x0C)     should be: \frac, \forall
 *   \b → backspace (0x08)     should be: \begin, \beta, \binom
 *   \a → bell (0x07)          should be: \alpha, \angle
 *   \v → vertical tab (0x0B)  should be: \vec, \vee
 *
 * NOTE: Normal whitespace characters (newline 0x0A, tab 0x09, carriage return 0x0D)
 * are NOT treated as corrupted LaTeX. These are legitimate formatting characters
 * found in markdown paragraphs, HTML/MathML indentation, etc.
 *
 * All functions are idempotent: running twice produces the same result.
 */

/**
 * Map of corrupted byte → possible LaTeX command continuations.
 * Each entry: [corruptedChar, [...possible completions]]
 * Order matters: longer/more-specific completions first for greedy matching.
 *
 * Only includes non-whitespace control characters that should never appear in text.
 * Newlines (0x0A), tabs (0x09), and carriage returns (0x0D) are excluded because
 * they are normal formatting characters.
 */
const CORRUPTION_MAP = [
  // \f (form feed, 0x0C) → \f...
  [
    '\f',
    [
      ['rac', 'frac'], // \frac{...}{...}
      ['orall', 'forall'], // \forall
      ['loor', 'floor'], // \floor
      ['lat', 'flat'], // \flat
      // Default: if \f is followed by something else or nothing
      ['', 'f']
    ]
  ],
  // \b (backspace, 0x08) → \b...
  [
    '\b',
    [
      ['egin', 'begin'], // \begin{...}
      ['eta', 'beta'], // \beta
      ['inom', 'binom'], // \binom
      ['ar', 'bar'], // \bar
      ['ig', 'big'], // \big
      ['ot', 'bot'], // \bot
      ['oxed', 'boxed'], // \boxed
      ['', 'b']
    ]
  ],
  // \a (bell, 0x07) → \a...
  // NOTE: must be the explicit \x07 escape. A '\a' string literal is just 'a' in JavaScript
  // (useless escape), which made this entry match every letter "a" whenever the text contained
  // any real control character — corrupting e.g. '\frac' (already repaired) into '\fr\ac'.
  [
    '\x07',
    [
      ['lpha', 'alpha'], // \alpha
      ['ngle', 'angle'], // \angle
      ['pprox', 'approx'], // \approx
      ['st', 'ast'], // \ast
      ['', 'a']
    ]
  ],
  // \v (vertical tab, 0x0B) → \v...
  [
    '\v',
    [
      ['ec', 'vec'], // \vec
      ['ee', 'vee'], // \vee
      ['', 'v']
    ]
  ],
  // \0 (null, 0x00) → \0... (rare but possible)
  ['\0', [['', '0']]]
]

/**
 * Repair corrupted LaTeX escape sequences in text.
 *
 * Only processes non-whitespace control characters (bell, backspace, form feed,
 * vertical tab, null). Normal whitespace (newlines, tabs, carriage returns)
 * is preserved as-is.
 *
 * @param {string} text - Text that may contain corrupted LaTeX
 * @returns {string} Text with repaired LaTeX sequences
 */
export function repairCorruptedLatex(text) {
  if (!text || typeof text !== 'string') return text

  // Quick check: only match non-whitespace control characters
  // 0x00-0x08 (null through backspace), 0x0B (vertical tab), 0x0C (form feed)
  // Excludes: 0x09 (tab), 0x0A (newline), 0x0D (carriage return)
  // eslint-disable-next-line no-control-regex
  if (!/[\x00-\x08\x0B\x0C]/.test(text)) return text

  let result = text

  for (const [corruptedChar, completions] of CORRUPTION_MAP) {
    if (!result.includes(corruptedChar)) continue

    // Process each occurrence of the corrupted character
    let i = 0
    let output = ''

    while (i < result.length) {
      if (result[i] === corruptedChar) {
        const remaining = result.slice(i + 1)
        let matched = false

        for (const [suffix, replacement] of completions) {
          if (suffix === '') {
            // Default fallback: just replace the control char with \<letter>
            output += '\\' + replacement
            matched = true
            break
          }
          if (remaining.startsWith(suffix)) {
            output += '\\' + replacement
            i += suffix.length // skip the suffix characters
            matched = true
            break
          }
        }

        if (!matched) {
          output += result[i]
        }
      } else {
        output += result[i]
      }
      i++
    }

    result = output
  }

  return result
}

/**
 * Replace a pattern with a LaTeX command, wrapping in $...$ when the match
 * is outside of existing math delimiters.  Inside a $...$ block the bare
 * command is used so we don't break the enclosing math expression.
 * @private
 */
function _replaceWithMathAware(text, pattern, latex) {
  return text.replace(pattern, (match, ...args) => {
    // The last two args from replace are offset and the full string
    const offset = typeof args[args.length - 2] === 'number' ? args[args.length - 2] : 0
    const before = text.slice(0, offset)
    const dollarCount = (before.match(/\$/g) || []).length
    // Odd count means we're inside a $...$ pair
    if (dollarCount % 2 === 1) {
      return latex
    }
    return `$${latex}$`
  })
}

/**
 * Apply alttext-to-LaTeX conversions to a plain text string (no HTML tags).
 * @private
 */
function _applyAlttextConversions(text) {
  let result = text

  // Fractions: "StartFraction X Over Y EndFraction" → \frac{X}{Y}
  result = result.replace(
    /StartFraction\s+(.+?)\s+Over\s+(.+?)\s+EndFraction/gi,
    (_, num, den) => `\\frac{${num.trim()}}{${den.trim()}}`
  )

  // Roots: "StartRoot X EndRoot" → \sqrt{X}
  result = result.replace(
    /StartRoot\s+(.+?)\s+EndRoot/gi,
    (_, content) => `\\sqrt{${content.trim()}}`
  )

  // Absolute value: "StartAbsoluteValue X EndAbsoluteValue" → |X|
  result = result.replace(
    /StartAbsoluteValue\s+(.+?)\s+EndAbsoluteValue/gi,
    (_, content) => `|${content.trim()}|`
  )

  // Parentheses: "left parenthesis X right parenthesis" → (X)
  result = result.replace(
    /left\s+parenthesis\s+(.+?)\s+right\s+parenthesis/gi,
    (_, content) => `(${content.trim()})`
  )

  // Exponents
  result = result.replace(/\bsquared\b/gi, '^{2}')
  result = result.replace(/\bcubed\b/gi, '^{3}')
  result = result.replace(/\bto the power of\s+(\w+)\b/gi, (_, exp) => `^{${exp}}`)

  // Common fractions
  result = result.replace(/\bone half\b/gi, '\\frac{1}{2}')
  result = result.replace(/\bone third\b/gi, '\\frac{1}{3}')
  result = result.replace(/\bone fourth\b/gi, '\\frac{1}{4}')
  result = result.replace(/\bthree fourths\b/gi, '\\frac{3}{4}')
  result = result.replace(/\btwo thirds\b/gi, '\\frac{2}{3}')

  // Greek letters (only standalone words, not inside other words or existing LaTeX commands)
  // Negative lookbehind (?<!\\) prevents doubling backslashes on already-escaped LaTeX like \pi
  result = result.replace(/(?<!\\)\bpi\b/g, '\\pi')
  result = result.replace(/(?<!\\)\btheta\b/gi, '\\theta')
  result = result.replace(/(?<!\\)\balpha\b/gi, '\\alpha')
  result = result.replace(/(?<!\\)\bbeta\b/gi, '\\beta')
  result = result.replace(/(?<!\\)\bgamma\b/gi, '\\gamma')
  result = result.replace(/(?<!\\)\bdelta\b/gi, '\\delta')
  result = result.replace(/(?<!\\)\bsigma\b/gi, '\\sigma')
  result = result.replace(/(?<!\\)\blambda\b/gi, '\\lambda')
  result = result.replace(/(?<!\\)\bmu\b/gi, '\\mu')
  result = result.replace(/(?<!\\)\bomega\b/gi, '\\omega')

  // Special symbols — LaTeX commands need $...$ when outside a math block,
  // but must stay bare when already inside $...$ (e.g. after MathML conversion).
  result = _replaceWithMathAware(result, /\binfinity\b/gi, '\\infty')
  result = _replaceWithMathAware(result, /\bplus or minus\b/gi, '\\pm')
  result = _replaceWithMathAware(result, /\bminus or plus\b/gi, '\\mp')
  result = _replaceWithMathAware(result, /\bgreater than or equal to\b/gi, '\\geq')
  result = _replaceWithMathAware(result, /\bless than or equal to\b/gi, '\\leq')
  // "greater than" / "less than" MUST come AFTER "or equal to" variants above
  result = result.replace(/\bgreater than\b/gi, '>')
  result = result.replace(/\bless than\b/gi, '<')
  result = _replaceWithMathAware(result, /\bnot equal to\b/gi, '\\neq')
  result = result.replace(/\bpercent sign\b/gi, '\\%')
  result = result.replace(/\bpercent\b/gi, '\\%')
  result = result.replace(/(\d)\s*\bdegrees?\b/gi, '$1°')

  return result
}

// Bare-LaTeX command word list + detector live in the shared math-detection module (CONTENT-5):
// one canonical copy for the client renderer, this repair module, and the server normalizer.
// Re-exported here so existing importers of latexRepair keep working.
import { BARE_LATEX_COMMANDS, hasBareLatexCommand } from '@shared/math/detection.js'
export { BARE_LATEX_COMMANDS, hasBareLatexCommand }

/**
 * Escape a bare percent sign inside a LaTeX math source string.
 *
 * In KaTeX/LaTeX `%` begins a comment, so an unescaped `%` silently swallows the
 * rest of the expression: `$45% = 0.45$` renders as just "45". LLM-generated
 * knowledge-node content frequently emits `45%` where it means `45\%`.
 *
 * Only `%` that is NOT already backslash-escaped is touched, so this is idempotent
 * (`\%` stays `\%`). MUST be applied to the extracted MATH SOURCE only — never to
 * surrounding prose, where "divide by 100" / "50% discount" must keep a literal `%`.
 *
 * @param {string} latex - raw math source (the text between `$...$`, `$$...$$`, etc.)
 * @returns {string} the math source with bare `%` escaped to `\%`
 */
export function escapeBareMathPercent(latex) {
  if (!latex || typeof latex !== 'string') return latex
  return latex.replace(/(?<!\\)%/g, '\\%')
}

// Repair-side regex built from the shared command list (the detection-side regex lives in the
// shared module next to the list itself).
const BARE_LATEX_REPLACE_RE = new RegExp(`(?<!\\\\)\\b(${BARE_LATEX_COMMANDS.join('|')})\\b`, 'g')

/**
 * Restore the leading backslash on bare LaTeX command words in a math source string.
 * Idempotent: the negative lookbehind leaves already-escaped commands (`\approx`) alone.
 * MUST be applied to extracted MATH SOURCE only, never to surrounding prose.
 *
 * @param {string} math - raw math source (the text between `$...$`, `$$...$$`, etc.)
 * @returns {string} the math source with bare command words backslash-prefixed
 */
export function restoreBareLatexCommands(math) {
  if (!math || typeof math !== 'string') return math
  return math.replace(BARE_LATEX_REPLACE_RE, '\\$1')
}

/**
 * Cheap trigger test for convertAlttextToLatex: matches when the text MAY contain a College Board
 * alttext-style English math description. Must include ALL trigger words for every conversion in
 * convertAlttextToLatex. Exported so the contentRenderer pipeline guard can use the same signal
 * for its fast-path skip without duplicating the keyword list.
 */
export const ALTTEXT_TRIGGER_RE =
  /StartFraction|EndFraction|StartRoot|EndRoot|StartAbsoluteValue|EndAbsoluteValue|\bsquared\b|\bcubed\b|\bone half\b|\bone third\b|\bone fourth\b|\bthree fourths\b|\btwo thirds\b|\bpi\b|\binfinity\b|\bdegrees?\b|\bleft\s+parenthesis\b|\bgreater\s+than\b|\bless\s+than\b|\bnot\s+equal\b|\bpercent\b|\bplus\s+or\s+minus\b|\bminus\s+or\s+plus\b|\bto\s+the\s+power\b/i

/**
 * Convert College Board MathML alttext notation to LaTeX.
 *
 * College Board uses English-like descriptions in MathML alttext attributes:
 *   "StartFraction X Over Y EndFraction" → \frac{X}{Y}
 *   "StartRoot X EndRoot" → \sqrt{X}
 *   "squared" → ^{2}
 *   "one half" → \frac{1}{2}
 *   etc.
 *
 * HTML-aware: only processes text content between HTML tags.
 * Content inside tag attributes (e.g., alt="x cubed") is left unchanged
 * to avoid corrupting HTML structure and alt text for accessibility.
 *
 * @param {string} text - Text containing alttext-style math descriptions
 * @returns {string} Text with descriptions converted to LaTeX
 */
export function convertAlttextToLatex(text) {
  if (!text || typeof text !== 'string') return text

  // Quick check: if no alttext keywords exist, return as-is.
  if (!ALTTEXT_TRIGGER_RE.test(text)) {
    return text
  }

  // If no HTML tags, process the whole string directly (fast path)
  if (!/<[^>]*>/.test(text)) {
    return _applyAlttextConversions(text)
  }

  // HTML-aware: split into tag segments (<...>) and text segments,
  // only apply conversions to text content between tags.
  const TAG_RE = /<[^>]*>/g
  const parts = []
  let lastIndex = 0
  let match

  while ((match = TAG_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(_applyAlttextConversions(text.slice(lastIndex, match.index)))
    }
    parts.push(match[0])
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) {
    parts.push(_applyAlttextConversions(text.slice(lastIndex)))
  }

  return parts.join('')
}

/**
 * Convert MathML <math> elements (with alttext attributes) to inline LaTeX.
 *
 * College Board SAT content uses MathML for math notation:
 *   <math alttext="x"><mi>x</mi></math>       → $x$
 *   <math alttext="40"><mn>40</mn></math>      → $40$
 *   <math alttext="x + 1"><mi>x</mi><mo>+</mo><mn>1</mn></math> → $x + 1$
 *
 * Native MathML rendering is inconsistent across browsers. Converting to
 * LaTeX ($...$) allows KaTeX to render math consistently.
 *
 * Handles both double-quoted and single-quoted alttext attributes.
 * If no alttext attribute is present, the <math> element is left as-is
 * for native browser rendering.
 *
 * @param {string} text - HTML text that may contain MathML <math> elements
 * @returns {string} Text with <math alttext="X">...</math> replaced by $X$
 */
export function convertMathMLToLatex(text) {
  if (!text || typeof text !== 'string') return text

  // Quick check: if no <math tag exists, return as-is (case-insensitive)
  if (!/<math/i.test(text)) return text

  // Match <math ...alttext="VALUE"...>CONTENT</math>
  // Handles: double quotes, single quotes, optional whitespace/newlines in content
  return text.replace(
    /<math\b[^>]*\balttext\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>[\s\S]*?<\/math>/gi,
    (match, alttextDouble, alttextSingle) => {
      const alttext = (alttextDouble ?? alttextSingle ?? '').trim()
      if (!alttext) return match
      return `$${alttext}$`
    }
  )
}
